import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import {
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Autocomplete,
  Paper,
  IconButton,
} from "@mui/material";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ChipsArray from "./ChipsArray";
import { useDropzone, FileRejection } from "react-dropzone";

export interface FileUploadData {
  files: File[] | undefined;
  labels: string[] | undefined;
  description: string | undefined;
}

export interface FileUploadConf {
  showExistingFiles: boolean;
  existingFiles: string[];
  showSetLabels: boolean;
  initialLabels: string[];
  selectedLabels: string[];
  showDescription: boolean;
  description: string;
  isFileRequired: boolean;
  maxFileSize?: number; // in MB
}

export interface FileUploadProps {
  visible: boolean;
  title: string;
  filters?: string;
  fileUploadConf: FileUploadConf;
  allowMultipleSelection?: boolean;
  onDismiss: () => void;
  onAccept: (data: FileUploadData) => Promise<void>;
}

export const FileUpload = ({
  visible,
  title,
  filters,
  fileUploadConf,
  allowMultipleSelection = true,
  onDismiss,
  onAccept,
}: FileUploadProps) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<string[]>(fileUploadConf.existingFiles);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [description, setDescription] = useState<string | undefined>("");
  const [labels, setLabels] = useState<string[] | undefined>([]);
  const refNewLabel = useRef<TextFieldProps>(null);
  const [error, setError] = useState<string>("");

  const maxFileSize = fileUploadConf.maxFileSize || 100; // Default 100MB
  const maxSizeBytes = maxFileSize * 1024 * 1024;

  useEffect(() => {
    setDescription(fileUploadConf.description ? fileUploadConf.description : "");
    setLabels(fileUploadConf.selectedLabels ? [...fileUploadConf.selectedLabels] : []);
  }, [fileUploadConf]);

  const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      if (rejection.errors[0]?.code === "file-too-large") {
        setError(`File ${rejection.file.name} exceeds maximum size of ${maxFileSize}MB`);
      } else if (rejection.errors[0]?.code === "file-invalid-type") {
        setError(`File ${rejection.file.name} is not a valid file type. Allowed: ${filters}`);
      } else {
        setError(rejection.errors[0]?.message || "File validation failed");
      }
      return;
    }

    setError("");
    if (allowMultipleSelection) {
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    } else {
      setSelectedFiles([acceptedFiles[0]]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: filters
      ? filters.split(",").reduce(
          (acc, ext) => {
            const trimmed = ext.trim();
            const extension = trimmed.startsWith(".") ? trimmed : `.${trimmed}`;
            acc[extension] = [];
            return acc;
          },
          {} as Record<string, string[]>,
        )
      : undefined,
    maxSize: maxSizeBytes,
    multiple: allowMultipleSelection,
    disabled: loading,
  });

  const dialogCloseHandler = (event: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const okButtonClickHandler = async () => {
    setLoading(true);
    await onAccept({ files: selectedFiles, labels, description });
    const newFileNames = selectedFiles.map((file) => file.name);
    setFiles([...files, ...newFileNames]);
    setSelectedFiles([]);
    setLoading(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return "0 Bytes";
    }
    const bytesPerUnit = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const unitIndex = Math.floor(Math.log(bytes) / Math.log(bytesPerUnit));
    return Math.round((bytes / Math.pow(bytesPerUnit, unitIndex)) * 100) / 100 + " " + sizes[unitIndex];
  };

  interface FileListProps {
    content: string[];
  }

  const FileList = ({ content }: FileListProps) => {
    return (
      <List dense={true}>
        {content && content.length ? (
          content.map((s, idx) => {
            return (
              <ListItem key={idx}>
                <ListItemText primary={s} />
              </ListItem>
            );
          })
        ) : (
          <ListItem disableGutters>
            <ListItemText primary="No files to display" />
          </ListItem>
        )}
      </List>
    );
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addLabel = () => {
    if (!refNewLabel.current?.value) {
      return;
    }

    const selectedLabels = labels ?? [];
    const newValue = refNewLabel.current.value as string;

    if (selectedLabels.includes(newValue)) {
      return;
    }

    selectedLabels.push(newValue);
    setLabels([...selectedLabels]);
  };

  return (
    <Dialog open={visible} onClose={dialogCloseHandler} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <DialogContentText>Please fill up the information below:</DialogContentText>
            <Typography component={"div"} variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              <fieldset>
                <legend>Files</legend>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Paper
                      {...getRootProps()}
                      sx={{
                        border: 2,
                        borderStyle: "dashed",
                        borderColor: isDragActive ? "primary.main" : error ? "error.main" : "grey.300",
                        backgroundColor: isDragActive ? "action.hover" : "background.paper",
                        padding: 3,
                        textAlign: "center",
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.6 : 1,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: loading ? "grey.300" : "primary.main",
                          backgroundColor: loading ? "background.paper" : "action.hover",
                        },
                      }}
                    >
                      <input {...getInputProps()} />
                      <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {isDragActive ? "Drop files here" : "Drag and drop files here"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        or click to browse
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {filters && `Accepted formats: ${filters}`}
                        {filters && " • "}Max {maxFileSize}MB per file
                      </Typography>
                      {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                          {error}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  {selectedFiles.length > 0 && (
                    <Grid size={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Files to Upload ({selectedFiles.length})
                        </Typography>
                        <List dense>
                          {selectedFiles.map((file, index) => (
                            <ListItem
                              key={index}
                              secondaryAction={
                                !loading && (
                                  <IconButton edge="end" onClick={() => handleRemoveFile(index)} size="small">
                                    <DeleteIcon />
                                  </IconButton>
                                )
                              }
                            >
                              <InsertDriveFileIcon sx={{ mr: 2, color: "text.secondary" }} />
                              <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  )}

                  {/* Existing Files */}
                  {fileUploadConf.showExistingFiles && (
                    <Grid size={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Uploaded Files
                        </Typography>
                        <FileList content={fileUploadConf.existingFiles} />
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </fieldset>
            </Typography>
            {fileUploadConf.showSetLabels && (
              <Typography component={"div"} variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                <fieldset>
                  <legend>Labels</legend>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid size={10}>
                      <Autocomplete
                        id="file-upload-labels"
                        freeSolo
                        options={fileUploadConf.initialLabels}
                        renderInput={(params) => <TextField {...params} label="Label" inputRef={refNewLabel} />}
                      />
                    </Grid>
                    <Grid size={2}>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={addLabel} fullWidth>
                        Add
                      </Button>
                    </Grid>
                    <Grid size={12}>
                      <ChipsArray data={labels ?? []} setChipData={setLabels} showDelete={true} />
                    </Grid>
                  </Grid>
                </fieldset>
              </Typography>
            )}
            {fileUploadConf.showDescription && (
              <Typography component={"div"} variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                <fieldset>
                  <legend>Description</legend>
                  <Grid container spacing={1}>
                    <Grid size={12}>
                      <TextField
                        id="file-upload-description"
                        multiline
                        rows={4}
                        fullWidth
                        onChange={(e) => setDescription(e.target.value)}
                        value={description}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onDismiss} color="error" disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={okButtonClickHandler}
          disabled={loading || (fileUploadConf.isFileRequired && files.length === 0 && selectedFiles.length === 0)}
        >
          {selectedFiles.length > 0 ? `Upload (${selectedFiles.length})` : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
